import React, { useEffect, useState } from "react";
import DiffView from "../../diff/DiffView/DiffView";
import ChangelogView from "../../changelog/ChangelogView/ChangelogView";
import {
  exportStatusPdf,
  fetchV2Schedules,
  getExportXerUrl,
} from "../../../api/clientV2";
import "./ExportPage.css";

type SubTab = "diff" | "changelog";

interface Props {
  /** Open the app-level Update Schedule overlay (kept here so the toolbar
   *  button and the nav badge share one flow that overlays the current view). */
  onOpenF9: () => void;
  /** Bumped by App after a forward pass applies, so Diff/Change Log refetch. */
  f9AppliedNonce?: number;
}

const ExportPage: React.FC<Props> = ({ onOpenF9, f9AppliedNonce = 0 }) => {
  const [activeSubTab, setActiveSubTab] = useState<SubTab>("diff");
  const [scheduleId, setScheduleId] = useState<number | null>(null);
  const [exportingPdf, setExportingPdf] = useState(false);

  useEffect(() => {
    fetchV2Schedules().then((schedules) => {
      if (schedules.length > 0) {
        setScheduleId(schedules[0].id);
      }
    });
  }, []);

  const handleExportXer = () => {
    if (scheduleId == null) return;
    window.open(getExportXerUrl(scheduleId), "_blank");
  };

  const handleExportStatusPdf = async () => {
    if (scheduleId == null || exportingPdf) return;
    setExportingPdf(true);
    try {
      await exportStatusPdf(scheduleId);
    } catch (err) {
      console.error("Failed to export status PDF", err);
      alert("Failed to export status PDF — see console for details.");
    } finally {
      setExportingPdf(false);
    }
  };

  return (
    <div className="export-page">
      <div className="export-page__toolbar">
        <div className="export-page__sub-tabs">
          <button
            className={`export-page__sub-tab ${activeSubTab === "diff" ? "active" : ""}`}
            onClick={() => setActiveSubTab("diff")}
          >
            Diff
          </button>
          <button
            className={`export-page__sub-tab ${activeSubTab === "changelog" ? "active" : ""}`}
            onClick={() => setActiveSubTab("changelog")}
          >
            Change Log
          </button>
        </div>

        <div className="export-page__actions">
          <button
            className="export-page__export-btn export-page__export-btn--secondary"
            onClick={onOpenF9}
            disabled={scheduleId == null}
            title="Record what happened (overdue + in-progress work), then preview and apply the reschedule from the data date"
          >
            Update Schedule
          </button>
          <button
            className="export-page__export-btn export-page__export-btn--secondary"
            onClick={handleExportStatusPdf}
            disabled={scheduleId == null || exportingPdf}
          >
            {exportingPdf ? "Generating…" : "Export Status PDF"}
          </button>
          <button
            className="export-page__export-btn"
            onClick={handleExportXer}
            disabled={scheduleId == null}
          >
            Export XER
          </button>
        </div>
      </div>

      <div className="export-page__content">
        {activeSubTab === "diff" ? (
          <DiffView key={`diff-${f9AppliedNonce}`} />
        ) : (
          <ChangelogView key={`changelog-${f9AppliedNonce}`} />
        )}
      </div>
    </div>
  );
};

export default ExportPage;
